

import { useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { FiUploadCloud } from "react-icons/fi";
import { AiOutlineFilePdf } from "react-icons/ai";
import { useSelector } from "react-redux";
import { Player } from "video-react";
import * as pdfjsLib from "pdfjs-dist/webpack"; // Import pdf.js
import "video-react/dist/video-react.css";

export default function UploadPdf({
  name,
  label,
  register,
  setValue,
  errors,
  video = false,
  pdf = false,
  viewData = null,
  editData = null,
  defaultValue = null,
  handleFileUpload,  // Function passed from parent for file upload
  accountType, // User role (assumed to be passed down)
}) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewSource, setPreviewSource] = useState(
    viewData ? viewData : editData ? editData : ""
  );
  const [uploadProgress, setUploadProgress] = useState(0); // For progress bar
  const inputRef = useRef(null);

  // Role-based validation (Only instructor can upload)
  // useEffect(() => {
  //   if (accountType !== "Instructor") {
  //     alert("You do not have permission to upload files.");
  //     // Optionally disable the file dropzone or other UI elements.
  //   }
  // }, [accountType]);

  const onDrop = (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      setSelectedFile(file);
      if (pdf) {
        generatePdfThumbnail(file);
      } else {
        previewFile(file);
      }
    }
  };

  useEffect(() => {
    if (defaultValue && pdf) {
      generatePdfThumbnail(defaultValue);
    }
  }, [defaultValue]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: pdf
      ? { "application/pdf": [".pdf"] }
      : video
      ? { "video/*": [".mp4"] }
      : { "image/*": [".jpeg", ".jpg", ".png"] },
    onDrop,
  });

  const previewFile = (file) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => {
      setPreviewSource(reader.result);
    };
  };

  const generatePdfThumbnail = async (fileOrUrl) => {
    let pdfData;
    if (typeof fileOrUrl === "string") {
      // Fetch PDF from URL
      const response = await fetch(fileOrUrl);
      const arrayBuffer = await response.arrayBuffer();
      pdfData = new Uint8Array(arrayBuffer);
    } else {
      // Handle uploaded file
      const reader = new FileReader();
      reader.readAsArrayBuffer(fileOrUrl);
      await new Promise((resolve) => {
        reader.onloadend = () => {
          pdfData = new Uint8Array(reader.result);
          resolve();
        };
      });
    }

    const pdf = await pdfjsLib.getDocument(pdfData).promise;
    const page = await pdf.getPage(1);

    const scale = 1.5;
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");

    canvas.width = viewport.width;
    canvas.height = viewport.height;

    await page.render({
      canvasContext: context,
      viewport: viewport,
    }).promise;

    setPreviewSource(canvas.toDataURL());
  };

  useEffect(() => {
    register(name, { required: true });
  }, [register, name]);

  useEffect(() => {
    setValue(name, selectedFile);
  }, [selectedFile, setValue, name]);

  const handleFileUploadProgress = async (file) => {
    // Mocking progress (replace with actual upload API)
    let progress = 0;
    const interval = setInterval(() => {
      progress += 10;
      setUploadProgress(progress);
      if (progress >= 100) clearInterval(interval);
    }, 500);
  };

  const clearFilePreview = () => {
    const confirmClear = window.confirm("Are you sure you want to clear the selected file?");
    if (confirmClear) {
      setPreviewSource("");
      setSelectedFile(null);
      setValue(name, null); // Reset form value
    }
  };

  return (
    <div className="flex flex-col space-y-2">
      <label className="text-sm text-richblack-800" htmlFor={name}>
        {label} {!viewData && <sup className="text-pink-200">*</sup>}
      </label>
      <div
        className={`${
          isDragActive ? "bg-pure-greys-250" : "bg-pure-greys-250"
        } flex min-h-[250px] cursor-pointer items-center justify-center rounded-md border-2 border-dotted border-richblack-500`}
        {...getRootProps()}
        onClick={(e) => {
          e.preventDefault();
          inputRef.current.click();
        }}
      >
        <input {...getInputProps()} ref={inputRef} style={{ display: "none" }} />
        {previewSource ? (
          <div className="flex w-full flex-col p-6">
            {!pdf ? (
              !video ? (
                <img
                  src={previewSource}
                  alt="Preview"
                  className="h-full w-full rounded-md object-cover"
                />
              ) : (
                <Player aspectRatio="16:9" playsInline src={previewSource} />
              )
            ) : (
              <img
                src={previewSource}
                alt="PDF Thumbnail"
                className="h-full w-full rounded-md object-cover"
              />
            )}
            {!viewData && (
              <button
                type="button"
                onClick={clearFilePreview}
                className="mt-3 text-pink-150 underline"
              >
                Cancel
              </button>
            )}
          </div>
        ) : (
          <div className="flex w-full flex-col items-center p-6">
            <div className="grid aspect-square w-14 place-items-center rounded-full bg-blue-400">
              <FiUploadCloud className="text-2xl text-blue-50" />
            </div>
            <p className="mt-2 max-w-[200px] text-center text-sm text-richblack-800">
              Drag and drop a {pdf ? "PDF" : !video ? "image" : "video"}, or click to{" "}
              <span className="font-semibold text-blue-50">Browse</span> a file
            </p>
            <ul className="mt-10 flex list-disc justify-between space-x-12 text-center text-xs text-richblack-600">
              <li>Aspect ratio 16:9</li>
              <li>Recommended size 1024x576</li>
            </ul>
          </div>
        )}
      </div>

      {uploadProgress > 0 && uploadProgress < 100 && (
        <div className="progress-bar mt-3">
          <div
            className="progress-bar-inner"
            style={{ width: `${uploadProgress}%` }}
          ></div>
        </div>
      )}

      {errors[name] && (
        <span className="ml-2 text-xs tracking-wide text-pink-200">
          {label} is required
        </span>
      )}
    </div>
  );
}
